class LyleProductCarouselTabbed extends HTMLElement {
  #abortController = null
  #carousels = new Map()
  #intersectionObserver = null

  connectedCallback() {
    this.#abortController = new AbortController()
    this.addEventListener('click', this.#handleClick, {
      signal: this.#abortController.signal,
    })
    this.addEventListener('keydown', this.#handleKeydown, {
      signal: this.#abortController.signal,
    })

    if (!('IntersectionObserver' in window)) {
      this.#initialize()
      return
    }

    this.#intersectionObserver = new IntersectionObserver(entries => {
      if (!entries.some(entry => entry.isIntersecting)) return

      this.#intersectionObserver?.disconnect()
      this.#intersectionObserver = null
      this.#initialize()
    }, { rootMargin: '400px 0px' })
    this.#intersectionObserver.observe(this)
  }

  disconnectedCallback() {
    this.#abortController?.abort()
    this.#abortController = null
    this.#intersectionObserver?.disconnect()
    this.#intersectionObserver = null

    for (const state of this.#carousels.values()) {
      clearInterval(state.autoplay)
      state.resizeObserver.disconnect()
    }
    this.#carousels.clear()
  }

  #initialize() {
    const firstTab = this.querySelector('[data-product-carousel-tabbed-tab]')
    const firstPanel = this.querySelector('[data-product-carousel-tabbed-panel]')

    if (firstTab) {
      this.#activateTab(firstTab.dataset.productCarouselTabbedTab)
    } else if (firstPanel) {
      this.#initCarousel(firstPanel)
    }
  }

  #handleClick = event => {
    const tab = event.target.closest('[data-product-carousel-tabbed-tab]')
    if (tab && this.contains(tab)) {
      this.#activateTab(tab.dataset.productCarouselTabbedTab)
      return
    }

    const arrow = event.target.closest('.splide__arrow')
    const carousel = arrow?.closest('[data-product-carousel-tabbed-carousel]')
    if (!carousel || !this.contains(carousel)) return

    this.#move(carousel, arrow.classList.contains('splide__arrow--next') ? 1 : -1)
  }

  #handleKeydown = event => {
    const tab = event.target.closest('[data-product-carousel-tabbed-tab]')
    if (!tab || !this.contains(tab)) return

    const tabs = [...this.querySelectorAll('[data-product-carousel-tabbed-tab]')]
    const currentIndex = tabs.indexOf(tab)
    let nextIndex = currentIndex

    if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length
    if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabs.length
    if (event.key === 'Home') nextIndex = 0
    if (event.key === 'End') nextIndex = tabs.length - 1
    if (nextIndex === currentIndex) return

    event.preventDefault()
    const nextTab = tabs[nextIndex]
    this.#activateTab(nextTab.dataset.productCarouselTabbedTab)
    nextTab.focus()
  }

  #activateTab(groupId) {
    for (const tab of this.querySelectorAll('[data-product-carousel-tabbed-tab]')) {
      const isActive = tab.dataset.productCarouselTabbedTab === groupId
      tab.classList.toggle('is-active', isActive)
      tab.setAttribute('aria-selected', String(isActive))
      tab.tabIndex = isActive ? 0 : -1
    }

    for (const panel of this.querySelectorAll('[data-product-carousel-tabbed-panel]')) {
      const isActive = panel.dataset.productCarouselTabbedPanel === groupId
      panel.hidden = !isActive

      if (!isActive) continue

      const carousel = panel.querySelector('[data-product-carousel-tabbed-carousel]')
      if (carousel && this.#carousels.has(carousel)) {
        requestAnimationFrame(() => this.#update(carousel))
      } else {
        this.#initCarousel(panel)
      }
    }
  }

  #autoplayDelay() {
    const seconds = Number(this.dataset.carouselSpeed)
    if (!Number.isFinite(seconds) || seconds <= 0) return 0
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return 0
    return seconds * 1000
  }

  #initCarousel(panel) {
    const carousel = panel.querySelector('[data-product-carousel-tabbed-carousel]')
    if (this.#carousels.has(carousel)) return

    const list = carousel?.querySelector('.splide__list')
    if (!carousel || !list?.children.length) return

    const track = carousel.querySelector('.splide__track')
    const resizeObserver = new ResizeObserver(() => this.#update(carousel))
    this.#carousels.set(carousel, { autoplay: 0, resizeObserver })
    resizeObserver.observe(carousel)
    track.addEventListener('scroll', () => this.#update(carousel), {
      passive: true,
      signal: this.#abortController.signal,
    })
    carousel.addEventListener('mouseenter', () => this.#stopAutoplay(carousel), {
      signal: this.#abortController.signal,
    })
    carousel.addEventListener('mouseleave', () => this.#startAutoplay(carousel), {
      signal: this.#abortController.signal,
    })
    carousel.addEventListener('focusin', () => this.#stopAutoplay(carousel), {
      signal: this.#abortController.signal,
    })
    carousel.addEventListener('focusout', () => this.#startAutoplay(carousel), {
      signal: this.#abortController.signal,
    })
    this.#update(carousel)
    this.#startAutoplay(carousel)
  }

  #move(carousel, direction) {
    const track = carousel.querySelector('.splide__track')
    const slides = [...carousel.querySelectorAll('.splide__slide')]
    const end = Math.max(0, slides.length - this.#perPage())
    const current = this.#index(track, slides)
    const next = direction > 0
      ? (current >= end ? 0 : current + 1)
      : (current <= 0 ? end : current - 1)

    track.scrollTo({
      left: slides[next]?.offsetLeft || 0,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    })
  }

  #update(carousel) {
    const track = carousel.querySelector('.splide__track')
    const slides = [...carousel.querySelectorAll('.splide__slide')]
    const end = Math.max(0, slides.length - this.#perPage())
    const index = Math.min(this.#index(track, slides), end)
    const progress = carousel.querySelector('.splide__progress')
    const bar = carousel.querySelector('.splide__progress__bar')
    const image = carousel.querySelector('.product-card__image-frame')
    const arrows = carousel.querySelector('.splide__arrows')

    carousel.classList.toggle('is-overflow', end > 0)
    if (progress) progress.hidden = end === 0
    if (bar) bar.style.width = end === 0 ? '0' : `${((index + 1) / (end + 1)) * 100}%`
    if (image && arrows) arrows.style.top = `${image.offsetHeight / 2}px`
  }

  #index(track, slides) {
    if (!track || !slides.length) return 0
    return slides.reduce((closest, slide, index) =>
      Math.abs(slide.offsetLeft - track.scrollLeft) < Math.abs(slides[closest].offsetLeft - track.scrollLeft)
        ? index
        : closest, 0)
  }

  #perPage() {
    return window.matchMedia('(width >= 1024px)').matches ? 4 : 2
  }

  #startAutoplay(carousel) {
    const state = this.#carousels.get(carousel)
    const delay = this.#autoplayDelay()
    if (!state || state.autoplay || !delay) return
    state.autoplay = setInterval(() => this.#move(carousel, 1), delay)
  }

  #stopAutoplay(carousel) {
    const state = this.#carousels.get(carousel)
    if (!state?.autoplay) return
    clearInterval(state.autoplay)
    state.autoplay = 0
  }
}

if (!customElements.get('lyle-product-carousel-tabbed')) {
  customElements.define('lyle-product-carousel-tabbed', LyleProductCarouselTabbed)
}
